import { answerQuestions, checkIsSafeCountry, useCountryList } from "@/hooks/useRequest/useSurveyRequest";
import { AnswerQuestionModel } from "@/interfaces/surveyTypes";
import log2sensors, { SensorsEventIds } from "@/utils/log2sensors";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import SurveyTitle from "../SurveyTitleV2";
import SurveyFooter from "../SurveyFooter";
import SelectArea from "@/components/Select";
import ValidateError from "@/containers/common/ValidateError";
import ContentContainer from "@/containers/common/ContentContainer";

interface NationalityProps {
	answerFieldToValueMap: Map<string, string>;
	questionFieldToIdMap: Map<string, string>;
	nationality: string;
	onNextClick: () => void;
}

interface IFormInput {
	taxResidence: string;
}

function TaxResidentPage(props: NationalityProps) {
	const router = useRouter();
	const { t } = useTranslation("survey");
	const { countryList } = useCountryList({ language: router.locale });
	const [loading, setLoading] = useState(false);
	const {
		control,
		handleSubmit,
		setValue,
		formState: { isValid, errors },
		getValues,
	} = useForm<IFormInput>({
		defaultValues: {
			taxResidence: "",
		},
	});

	useEffect(() => {
		if (getValues().taxResidence) return;

		setValue("taxResidence", props.nationality, { shouldValidate: true });
	}, [props.nationality]);

	useEffect(() => {
		const countryValue = props.answerFieldToValueMap.get("taxResidence");
		if (!countryValue) return;
		setValue("taxResidence", countryValue, { shouldValidate: true });
	}, []);

	const onSubmit: SubmitHandler<IFormInput> = async (data) => {
		try {
			const countryQuestionId = props.questionFieldToIdMap.get("taxResidence");
			if (!countryQuestionId) {
				throw Error("missing taxResidence questionId");
			}

			const payload: AnswerQuestionModel[] = [
				{
					answer: data.taxResidence,
					questionId: countryQuestionId,
				},
			];

			setLoading(true);
			await answerQuestions({ payload });
			props.onNextClick();
		} catch (error) {
			console.log(error);
		} finally {
			setLoading(false);
			log2sensors(SensorsEventIds.AO_TAX_RESIDENT_NATIONALITY_NEXTPAGE);
		}
	};

	useEffect(() => {
		log2sensors(SensorsEventIds.AO_TAX_RESIDENT_NATIONALITY_VIEW);
	}, []);

	return (
		<ContentContainer
			className="px-4.5"
			animation
			header={
				<SurveyTitle
					title={t("what-is-your-tax-residence")}
					subtitle={t("most-users-have-their-tax-residence-aligned-with-their-nationality")}
				/>
			}
			footer={
				<SurveyFooter
					loading={loading}
					disabled={!isValid}
					onNextBtnClick={handleSubmit(onSubmit)}
					onPrivacyClick={() => log2sensors(SensorsEventIds.AO_TAX_RESIDENT_NATIONALITY_PRAVICY)}
					onSecureClick={() => log2sensors(SensorsEventIds.AO_TAX_RESIDENT_NATIONALITY_SECURITY)}
				/>
			}
		>
			<div className="h-12" />
			<Controller
				name="taxResidence"
				control={control}
				rules={{
					required: t("country-region-cannot-be-empty")
				}}
				defaultValue=""
				render={({ field }) => (
					<SelectArea
						{...field}
						placeholder={t("country-region")}
						defaultValue={field.value}
						options={countryList}
						onChange={(value) => {
							field.onChange(value);
							setValue("taxResidence", value, { shouldValidate: true });
							log2sensors(SensorsEventIds.AO_NATIONALITY_NATIONALITY);
						}}
					/>
				)}
			/>

			<ValidateError error={errors.taxResidence} />
		</ContentContainer>
	);
}

export default TaxResidentPage;
